<template>
  <div class="transaction_header">
    <button class="transaction_block-close" @click="onClose">
      {{ $t('common.close') }}
    </button>
    <div class="transaction_header-title">
      {{ getBillText }} - {{ transaction.created_at | date('DD.MM.YYYY HH:mm') }}
    </div>
    <div
      class="transaction_header-info"
      :class="{ 'editable': isEditableAmount }"
      @click.prevent="onEditAmount"
    >
      <span
        class="amount_wrapper"
        v-html="formattedBillAmount"
      ></span>
      <span class="pencil"></span>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex';

import { formatAmount } from 'lib/utils/amount';

import { CASUAL, SUBSCRIPTION, TARIFF, TICKET, TIPS } from './const';

export default {
  name: 'BillCardHeader',
  props: {
    transaction: {
      type: Object,
      required: true,
    },
    permission: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    billType() {
      return this.transaction.type;
    },
    isCasual() {
      return this.billType === CASUAL;
    },
    isTips() {
      return this.billType === TIPS;
    },
    isMulti() {
      return this.transaction.multiBill;
    },
    billTariffs() {
      return this.transaction.billTariffs;
    },
    getBillText() {
      if (this.billType === SUBSCRIPTION) {
        return this.$t('bill.subscription');
      }

      if (this.billType === TARIFF) {
        return this.$t('bill.with.tariffs');
      }

      if (this.billType === TICKET) {
        return this.$t('bill.ticket');
      }

      if (this.isTips) {
        return this.$t('bill.tips');
      }

      return this.isMulti ? this.$t('common.multibill') : this.$t('common.bill');
    },
    isEditableAmount() {
      if (!this.permission) {
        return false;
      }

      if (!this.isMulti) {
        return false;
      }

      return this.isCasual || this.isTips;
    },
    formattedBillAmount() {
      const { currency } = this.SELECTED_TRANSACTION;

      if (this.billType === CASUAL) {
        const { amount, commission } = this.billTariffs[0];

        if (this.$validate.isNull(amount)) {
          return this.$t('any.amount');
        }

        return formatAmount(amount + commission, currency);
      }

      const amountList = this.getAmountList();

      if (this.$validate.isEmptyArray(amountList)) {
        return this.$t('any.amount');
      }

      if (this.isTips) {
        return this.$t('bill.tips.header.amount', {
          amount: formatAmount(amountList[0], currency),
        });
      }

      if (this.$validate.isFixedLengthArray(amountList, 1)) {
        return formatAmount(amountList[0], currency);
      }

      return this.$t('amount.from', {
        displayAmount: formatAmount(Math.min(...amountList), currency),
      });
    },
    ...mapState(['SELECTED_TRANSACTION']),
  },
  methods: {
    onClose() {
      this.CLOSE_TRANSACTION();
    },
    onEditAmount() {
      if (!this.isEditableAmount) {
        return false;
      }

      const limits = this.isTips ? {
        min_amount: null,
        max_amount: 100000000000,
      } : null;

      this.SET_MODAL_PARAMS({
        type: 'MULTIBILL_EDIT',
        params: {
          show: true,
          bill: this.transaction,
          tariffId: this.billTariffs[0].id,
          tips: this.transaction.tipsVariants?.join(',') || null,
          limits,
        },
      });
    },
    getAmountList() {
      return this.billTariffs.reduce((acc, curr) => {
        const { amount, commission } = curr;

        if (this.$validate.isNotNull(amount)) {
          acc.push(amount + commission);
        }

        return acc;
      }, []);
    },
    ...mapMutations(['CLOSE_TRANSACTION', 'SET_MODAL_PARAMS']),
  },
};
</script>

<style lang="sass" scoped>
  @mixin square($val)
    width: $val
    height: $val

  @mixin headerIcons($icon, $color: #212121)
    &::before
      background-image: get-icon($icon, $color, $transactionHeaderIcons)
      background-repeat: no-repeat

  %icon-bg
    background:
      color: transparent
      repeat: no-repeat
      position: center

  %button-icon
    position: absolute
    +square(2rem)
    @extend %icon-bg

  .transaction_block
    overflow: hidden
    border-radius: 0.36rem
    position: relative
    overflow-y: auto
    max-height: 80vh
    +adaptive(mobile)
      max-height: calc(100vh - 1.24rem)

    #{theme("eclipse")} &
      color: #212121
      background-color: $pure-white

    #{theme("obsidian")} &
      color: #212121
      background-color: #F2F2F2

  .transaction_block-close
    @extend %button-icon
    font-size: 0
    line-height: 1.44rem
    top: 1.5rem
    right: 2rem
    background-image: get-icon(close, #212121, $misc)
    background-size: 100%
    transition: .2s ease
    +adaptive(mobile-xl)
      right: 1rem
      z-index: 4

    &:hover
      transform: scale(.9)

  .transaction_header
    padding: 2rem
    padding-left: 5.6rem
    font-weight: 600
    +adaptive(mobile)
      padding-right: 1rem
      padding-left: 4rem
      padding-bottom: 1.5rem
    +adaptive(laptop-start)
      position: sticky
      top: 0
      z-index: 3

    &::before
      @extend %button-icon
      content: ''
      left: 2rem
      top: 1.5rem
      .transfer &
        top: 1.7rem
      +adaptive(mobile)
        left: 1rem

    .bill &
      background: linear-gradient(39.13deg, rgba(252, 212, 0, 1) 0%, rgba(254, 135, 26, 1) 100%), rgba(255, 255, 255, 1)
      +headerIcons(bill)

    .multi-on &
      +headerIcons(multi-on)

    .multi-off &
      +headerIcons(multi-off)
      filter: grayscale(1)

    .subscription &
      +headerIcons(subscription)

    .tariff &
      +headerIcons(tariff)

    .ticket &
      +headerIcons(ticket)

    .tips &
      +headerIcons(tips-circle)

  .transaction_header-title
    font-size: .9rem
    padding-bottom: .5rem
    padding-right: 1.5rem

  .transaction_header-info
    font-size: 1.4rem

  .transaction_header-info.editable
    display: inline-block
    cursor: pointer

    .amount_wrapper
      border-bottom: 1px solid #1e1d2f

    .pencil
      background-image: get-icon(pencil, black)
      background-repeat: no-repeat
      display: inline-block
      vertical-align: text-top
      margin-left: 2px
      width: 20px
      height: 20px
</style>
